// LLM Response Cache
// Caches LLM responses in localStorage with request hash as key

import type { LocaleCode, Difficulty } from "./api/client";

interface CacheEntry {
  response: string;
  timestamp: number;
  provider: string;
  model: string;
}

interface CacheRequest {
  topic: string;
  nativeLanguage: LocaleCode;
  targetLanguage: LocaleCode;
  difficulty: Difficulty;
}

const CACHE_PREFIX = "llm-cache-";
const CACHE_INDEX_KEY = "llm-cache-index";
const MAX_CACHE_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_CACHE_ENTRIES = 100;

// Create a hash of the request for cache key
function hashRequest(request: CacheRequest): string {
  const str = JSON.stringify({
    topic: request.topic.toLowerCase().trim(),
    native: request.nativeLanguage.toLowerCase().trim(),
    target: request.targetLanguage.toLowerCase().trim(),
    difficulty: request.difficulty.toLowerCase().trim(),
  });

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

// Get cache index (list of all cached keys)
function getCacheIndex(): string[] {
  try {
    const index = localStorage.getItem(CACHE_INDEX_KEY);
    return index ? JSON.parse(index) : [];
  } catch {
    return [];
  }
}

// Update cache index
function updateCacheIndex(keys: string[]): void {
  try {
    localStorage.setItem(CACHE_INDEX_KEY, JSON.stringify(keys));
  } catch {
    // Storage full, try to clean up
    cleanupOldEntries();
  }
}

// Clean up old cache entries
function cleanupOldEntries(): void {
  const index = getCacheIndex();
  const now = Date.now();
  const validKeys: string[] = [];

  for (const key of index) {
    try {
      const entry = localStorage.getItem(CACHE_PREFIX + key);
      if (entry) {
        const parsed: CacheEntry = JSON.parse(entry);
        if (now - parsed.timestamp < MAX_CACHE_AGE_MS) {
          validKeys.push(key);
        } else {
          localStorage.removeItem(CACHE_PREFIX + key);
        }
      }
    } catch {
      localStorage.removeItem(CACHE_PREFIX + key);
    }
  }

  // If still too many, remove oldest
  if (validKeys.length > MAX_CACHE_ENTRIES) {
    const toRemove = validKeys.slice(0, validKeys.length - MAX_CACHE_ENTRIES);
    for (const key of toRemove) {
      localStorage.removeItem(CACHE_PREFIX + key);
    }
    validKeys.splice(0, toRemove.length);
  }

  updateCacheIndex(validKeys);
}

// Get cached response
export function getCachedResponse(request: CacheRequest): string | null {
  try {
    const key = hashRequest(request);
    const entry = localStorage.getItem(CACHE_PREFIX + key);

    if (!entry) return null;

    const parsed: CacheEntry = JSON.parse(entry);

    // Check if expired
    if (Date.now() - parsed.timestamp > MAX_CACHE_AGE_MS) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    return parsed.response;
  } catch {
    return null;
  }
}

